import React, { useCallback, useEffect, useRef, useState } from 'react';
import BrandDropdown from 'shared/components/ui/BrandDropdown';
import SegmentedTabs from 'shared/components/ui/SegmentedTabs';
import { X, Trash2, Play, Loader2 } from 'lucide-react';
import { useTheme } from 'shared/theme/ThemeContext';
import ModelParamsPopover from 'shared/components/ui/ModelParamsPopover';
import VariableEditor, { insertAtCursor, textWithInsert } from './VariableEditor';
import SchemaRail from './SchemaRail';
import { SpanVariablesSection } from './SpanVariables';
import FilterRulesEditor from './FilterRulesEditor';
import OutputFieldsEditor from './OutputFieldsEditor';
import ContextFieldsEditor from './ContextFieldsEditor';
import EdgesEditor from './EdgesEditor';
import OutcomesEditor from './OutcomesEditor';
import SemanticEditor from './SemanticEditor';
import DecisionEditor from './DecisionEditor';
import DecisionRoutes from './DecisionRouteEditor';
import TagInput from './TagInput';
import * as outcomes from './outcomes';
import { ProviderDropdown, ModelDropdown } from './modelParts';
import { NodeMark } from './EvalNode';
import { kindOf, kindMeta } from './nodeKinds';
import { numericUpstreamFields, displayName, stepsGroup } from './graphModel';
import { Step as StepRow } from '../components/ResultPath';

/**
 * A node's settings, in a panel beside the canvas — the canvas shrinks to
 * make room, the step being edited stays visible in the graph.
 *
 * Inside, each kind gets the SAME editor the current evals have for that
 * kind: Python → a code editor with the trace fields beside it; LLM judge →
 * provider, model, params and a prompt editor with {{field}} pills; Filter →
 * rules; Human → instructions and the options a grader picks from; Verdict →
 * pass/fail, reason, which score to show.
 *
 * Tabs: Settings · Output & routing · Test.
 */

const MIN_W = 380;
const MAX_W = 820;
const DEFAULT_W = 520;
const WIDTH_KEY = 'trodo.eval3NodePanel.width';

function readWidth() {
  try {
    const raw = Number(localStorage.getItem(WIDTH_KEY));
    if (Number.isFinite(raw) && raw >= MIN_W && raw <= MAX_W) return raw;
  } catch (_) { /* private mode */ }
  return DEFAULT_W;
}

/**
 * A titled block. `hint` is the explanation — it lives on the title as a
 * tooltip, not as a line of prose under it. A panel you have to READ before
 * you can use it is a panel that is in the way; the words are still there
 * for the one time someone wants them.
 */
function Section({ title, hint, children }) {
  return (
    <section className="mb-5">
      {title && (
        <h3 title={hint || undefined}
          className={`mb-1.5 text-[11px] font-semibold uppercase tracking-wide text-[color:var(--text-primary)] ${hint ? 'cursor-help decoration-dotted decoration-[color:var(--text-disabled)] underline-offset-4 hover:underline' : ''}`}>
          {title}
        </h3>
      )}
      {children}
    </section>
  );
}

function Field({ label, children }) {
  return (
    <label className="block">
      <div className="mb-1 text-[11px] font-semibold uppercase tracking-wide text-[color:var(--text-secondary)]">{label}</div>
      {children}
    </label>
  );
}

// ---------------------------------------------------------------------------
// Per-kind editors
// ---------------------------------------------------------------------------

function PythonEditor({ node, update, schema, theme, readOnly, graph, nodeId }) {
  const editorRef = useRef(null);
  const before = graph && nodeId ? stepsGroup(graph, nodeId) : [];
  return (
    <>
      <Section title="Python code" hint="def evaluate(t) — return the outcome (True/False, a number, or one of the options). To pass something on, return it second: return ok, {'excerpts': [...]}. No network.">
        <div className="overflow-hidden rounded-md border border-[color:var(--border-default)]">
          <VariableEditor height="360px" language="python" mode="python" theme={theme} editorRef={editorRef} readOnly={readOnly}
            value={node.config?.source || ''} onChange={(v) => update({ config: { ...node.config, source: v } })} />
        </div>
      </Section>
      {/* What the step passes on, apart from what it decides. */}
      <Section title="Context" hint="what this step passes on to later steps. A judge reads it as {{steps.<this step>.<name>}}, Python as t.steps.<this step>.<name>. Return it as the second value: return ok, {'excerpts': [...]}">
        <ContextFieldsEditor value={node.context} readOnly={readOnly} taken={Object.keys(node.output || {})}
          onChange={(context) => update({ context })} />
      </Section>
      <Section title="Fields" hint="drag into the code, or click to insert at the caret">
        <SchemaRail schema={schema} extraGroups={before} refOf={(f) => f.python} onPick={(f) => insertAtCursor(editorRef.current, f.python)}
          hint="text(x) turns any field into a string. t.spans.where(type='tool') filters spans." />
      </Section>
    </>
  );
}

function JudgeEditor({ node, update, schema, theme, readOnly, integrations, modelsFor, graph, nodeId, scopes, target, teamId, agentName }) {
  const editorRef = useRef(null);
  const before = graph && nodeId ? stepsGroup(graph, nodeId) : [];
  const cfg = node.config || {};
  const integrationId = cfg.integration_id ?? '';
  const selected = (integrations || []).find((i) => String(i.id) === String(integrationId));
  const prompt = typeof cfg.prompt === 'string' ? { user: cfg.prompt } : (cfg.prompt || {});
  return (
    <>
      <Section title="Model" hint={cfg.integration_id == null ? 'this judge needs a provider and a model' : undefined}>
        <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
          <Field label="Provider">
            <ProviderDropdown integrations={integrations} value={integrationId} disabled={readOnly}
              onChange={(id) => {
                const next = integrations.find((i) => String(i.id) === String(id));
                const models = modelsFor(next);
                // Keep the model if the new provider has it; else its default; else its only model.
                const model = models.includes(cfg.model) ? cfg.model
                  : next?.default_model && models.includes(next.default_model) ? next.default_model
                    : models.length === 1 ? models[0] : undefined;
                update({ config: { ...cfg, integration_id: Number(id), model } });
              }} />
            {(integrations || []).length === 0 && <div className="mt-1 text-[11px] text-warning">No providers yet. Add one in Settings → Models first.</div>}
          </Field>
          <Field label="Model">
            <div className="flex items-center gap-2">
              <div className="min-w-0 flex-1">
                <ModelDropdown integration={selected} models={modelsFor(selected)} value={cfg.model || ''} disabled={readOnly}
                  onChange={(m) => update({ config: { ...cfg, integration_id: cfg.integration_id ?? selected?.id, model: m } })} />
              </div>
              <ModelParamsPopover value={cfg.model_params || {}} disabled={readOnly} onChange={(p) => update({ config: { ...cfg, model_params: p } })} />
            </div>
          </Field>
        </div>
      </Section>
      <Section title="Judge prompt" hint="the model answers with the declared output fields as JSON; that instruction is appended for you">
        <div className="overflow-hidden rounded-md border border-[color:var(--border-default)]">
          <VariableEditor height="300px" language="markdown" mode="prompt" theme={theme} editorRef={editorRef} readOnly={readOnly}
            value={prompt.user || ''} onChange={(v) => update({ config: { ...cfg, prompt: { ...prompt, user: v } } })} />
        </div>
        <details className="mt-1.5">
          <summary className="cursor-pointer text-[11px] text-[color:var(--text-muted)]">System prompt (optional)</summary>
          <textarea value={prompt.system || ''} readOnly={readOnly} rows={3} aria-label="System prompt"
            onChange={(e) => update({ config: { ...cfg, prompt: { ...prompt, system: e.target.value || undefined } } })}
            className="input mt-1 min-h-[70px] w-full" />
        </details>
      </Section>
      {/* Spans are chosen, never dumped: a span variable says which spans,
          which of them and what to read, and the prompt reads {{spans.NAME}}. */}
      {(target || schema?.target || 'run') !== 'span' && (
        <Section title="Spans" hint="choose spans of the run for the prompt: which ones, which of them, what to read, and what to do when none match">
          <SpanVariablesSection vars={cfg.span_vars || []} readOnly={readOnly} scopes={scopes} target={target || schema?.target || 'run'} teamId={teamId} agentName={agentName}
            onInsert={(ref) => insertAtCursor(editorRef.current, ref)}
            onChange={(vars, ref) => {
              const next = { ...cfg };
              if (vars.length) next.span_vars = vars; else delete next.span_vars;
              if (ref) next.prompt = { ...prompt, user: textWithInsert(editorRef.current, prompt.user || '', ref) };
              update({ config: next });
            }} />
        </Section>
      )}
      <Section title="Fields" hint="drag into the prompt, or click to insert">
        <SchemaRail schema={schema} extraGroups={before} hideTypes={['list']} refOf={(f) => f.prompt} onPick={(f) => insertAtCursor(editorRef.current, f.prompt)} maxHeight={200} />
      </Section>
    </>
  );
}

function FilterEditor({ node, update, schema, scopes, target, readOnly, teamId }) {
  // The eval's target is the root level; `scopes` carries every level a
  // block can open (a run's spans, a conversation's runs), same as Settings.
  const fields = (schema?.groups || []).flatMap((g) => g.fields).filter((f) => f.type !== 'list');
  return (
    <Section title="Filters" hint="evaluated in-process on every event, no sandbox and no model">
      <FilterRulesEditor value={node.config?.filter} target={target || schema?.target || 'run'} scopes={scopes} fields={fields} readOnly={readOnly} teamId={teamId}
        onChange={(filter) => update({ config: { ...node.config, filter } })} />
    </Section>
  );
}

/**
 * A human step asks a person for exactly one thing. There are three shapes
 * that thing takes, and the shape decides the control the grading queue
 * gives the reviewer: buttons, a pair of thumbs, or a scale.
 *
 * Changing the shape changes the field the routes below read, so the routes
 * cannot survive it — they are reset to the one they already fell through to,
 * which keeps the graph connected while the rules are rebuilt.
 */
const ANSWER_SHAPES = [
  { value: 'enum', label: 'Pick one', hint: 'a list of options; each one is a route below' },
  { value: 'boolean', label: 'Yes or no', hint: 'two options, shown as thumbs in the queue' },
  { value: 'number', label: 'A rating', hint: 'a number in a range; the routes threshold it' },
];
const SHAPE_DEFAULTS = {
  enum: { field: 'choice', spec: { type: 'enum', values: ['yes', 'no'] } },
  boolean: { field: 'acceptable', spec: { type: 'boolean' } },
  number: { field: 'rating', spec: { type: 'number', min: 1, max: 5, step: 1 } },
};

function HumanEditor({ node, update, readOnly }) {
  const qc = node.config?.queue_config || {};
  const [field, spec] = Object.entries(node.output || {})[0] || ['choice', { type: 'enum', values: [] }];
  const shape = ANSWER_SHAPES.some((s) => s.value === spec.type) ? spec.type : 'enum';
  const setConfig = (next) => update({ config: { ...node.config, queue_config: { ...qc, ...next } } });
  const setSpec = (next) => update({ output: { [field]: { ...spec, ...next } } });

  const setShape = (value) => {
    if (value === shape) return;
    const d = SHAPE_DEFAULTS[value];
    const fallback = (node.edges || []).find((e) => e.default)?.default || (node.edges || [])[0]?.to;
    update({
      output: { [d.field]: d.spec },
      ...(fallback ? { edges: [{ default: fallback }] } : {}),
    });
  };

  return (
    <>
      <Section title="Instructions for graders" hint="shown beside the trace in the grading queue">
        <textarea value={qc.instructions || ''} readOnly={readOnly} rows={5} aria-label="Instructions for graders"
          onChange={(e) => setConfig({ instructions: e.target.value })} className="input min-h-[120px] w-full" />
      </Section>

      <Section title="What the grader answers" hint="the shape of the answer decides the control they get, and the routes below read it">
        <div role="radiogroup" aria-label="What the grader answers" className="inline-flex rounded-md border border-[color:var(--border-default)] bg-[color:var(--surface-sunken)] p-0.5">
          {ANSWER_SHAPES.map((sh) => (
            <button key={sh.value} type="button" role="radio" aria-checked={shape === sh.value} disabled={readOnly} title={sh.hint}
              onClick={() => setShape(sh.value)}
              className={`h-7 rounded px-3 text-[12px] font-medium ${shape === sh.value ? 'bg-[color:var(--surface-base)] text-[color:var(--text-primary)] shadow-token-sm' : 'text-[color:var(--text-muted)]'}`}>
              {sh.label}
            </button>
          ))}
        </div>
      </Section>

      {shape === 'enum' && (
        <Section title="Options" hint="what the grader picks from; each option is a route below">
          <TagInput value={spec.values || []} readOnly={readOnly} ariaLabel="Grader options" placeholder="Type an option and press Enter" min={1}
            onChange={(values) => setSpec({ values })} />
        </Section>
      )}

      {shape === 'boolean' && (
        <Section title="Options">
          <p className="text-[12px] text-[color:var(--text-muted)]">Yes or no. The queue shows a thumb for each; the routes below read <code className="font-mono">{field}</code> as true or false.</p>
        </Section>
      )}

      {shape === 'number' && (
        <>
          <Section title="The scale" hint="a grader can only land on values the step divides into">
            <div className="grid grid-cols-3 gap-2">
              {[['min', 'Lowest'], ['max', 'Highest'], ['step', 'Moves in']].map(([key, label]) => (
                <label key={key} className="flex flex-col gap-1 text-[11px] text-[color:var(--text-muted)]">
                  {label}
                  <input type="number" value={spec[key] ?? ''} readOnly={readOnly} aria-label={label} className="input w-full"
                    onChange={(e) => {
                      const n = e.target.value === '' ? undefined : Number(e.target.value);
                      setSpec({ [key]: Number.isFinite(n) ? n : undefined });
                    }} />
                </label>
              ))}
            </div>
            <p className="mt-1.5 text-[11px] text-[color:var(--text-muted)]">
              Leave <em>Moves in</em> empty for a scale a grader drags rather than presses.
            </p>
          </Section>
          <Section title="What the ends mean" hint="optional; shown beside the scale so a 1 is not a bare 1">
            <div className="grid grid-cols-2 gap-2">
              <input value={qc.labels?.min || ''} readOnly={readOnly} aria-label="What the lowest means" placeholder="unusable" className="input w-full"
                onChange={(e) => setConfig({ labels: { ...(qc.labels || {}), min: e.target.value || undefined } })} />
              <input value={qc.labels?.max || ''} readOnly={readOnly} aria-label="What the highest means" placeholder="perfect" className="input w-full"
                onChange={(e) => setConfig({ labels: { ...(qc.labels || {}), max: e.target.value || undefined } })} />
            </div>
          </Section>
        </>
      )}
    </>
  );
}

function VerdictEditor({ graph, nodeId, node, update, readOnly }) {
  const cfg = node.config || {};
  const scores = numericUpstreamFields(graph, nodeId);
  const scoreKey = cfg.score_from ? `${cfg.score_from.node_id}.${cfg.score_from.field}` : '';
  return (
    <>
      <Section title="Verdict">
        <div role="radiogroup" aria-label="Verdict" className="inline-flex rounded-md border border-[color:var(--border-default)] bg-[color:var(--surface-sunken)] p-0.5">
          {[[true, 'Pass'], [false, 'Fail']].map(([v, label]) => (
            <button key={label} type="button" role="radio" aria-checked={(cfg.passed !== false) === v} disabled={readOnly}
              onClick={() => update({ config: { ...cfg, passed: v, reason_code: v ? undefined : cfg.reason_code } })}
              className={`h-7 rounded px-4 text-[12px] font-medium ${(cfg.passed !== false) === v ? 'bg-[color:var(--surface-base)] text-[color:var(--text-primary)] shadow-token-sm' : 'text-[color:var(--text-muted)]'}`}>
              {label}
            </button>
          ))}
        </div>
      </Section>
      {cfg.passed === false && (
        <Section title="Reason code" hint="a short tag results are grouped by">
          <input value={cfg.reason_code || ''} readOnly={readOnly} aria-label="Reason code" placeholder="LOW_SCORE"
            onChange={(e) => update({ config: { ...cfg, reason_code: e.target.value.toUpperCase().replace(/[^A-Z0-9_]/g, '_') || undefined } })} className="input w-full font-mono" />
        </Section>
      )}
      <Section title="Display score" hint="optional: a number from a node on every path here, shown beside the verdict">
        <BrandDropdown ariaLabel="Display score" value={scoreKey} disabled={readOnly}
          onChange={(v) => {
            const next = { ...cfg };
            if (!v) delete next.score_from; else { const [node_id, f] = v.split('.'); next.score_from = { node_id, field: f }; }
            update({ config: next });
          }}
          options={[{ value: '', label: 'No score, pass/fail only' }, ...scores.map((s) => ({ value: `${s.node_id}.${s.field}`, label: `${displayName(graph, s.node_id)} · ${s.field} (${s.min}–${s.max})` }))]} />
      </Section>
    </>
  );
}

/**
 * The editor for one kind of step, the same one wherever a step is edited:
 * this panel beside the canvas, and the simple eval's form.
 */
export function KindEditor({ kind, node, update, graph, nodeId, readOnly, schema, scopes, target, integrations, modelsFor, teamId, agentName }) {
  const { theme } = useTheme();
  if (kind === 'python') return <PythonEditor node={node} update={update} schema={schema} theme={theme} readOnly={readOnly} graph={graph} nodeId={nodeId} />;
  if (kind === 'llm_judge') return <JudgeEditor node={node} update={update} schema={schema} theme={theme} readOnly={readOnly} integrations={integrations} modelsFor={modelsFor} graph={graph} nodeId={nodeId} scopes={scopes} target={target} teamId={teamId} agentName={agentName} />;
  if (kind === 'decision_model') return <DecisionEditor node={node} update={update} schema={schema} theme={theme} readOnly={readOnly} graph={graph} nodeId={nodeId} target={target || schema?.target || 'run'} teamId={teamId} scopes={scopes} agentName={agentName} />;
  if (kind === 'ui_filter') return <FilterEditor teamId={teamId} node={node} update={update} schema={schema} scopes={scopes} target={target} readOnly={readOnly} />;
  if (kind === 'human') return <HumanEditor node={node} update={update} readOnly={readOnly} />;
  if (kind === 'verdict') return <VerdictEditor graph={graph} nodeId={nodeId} node={node} update={update} readOnly={readOnly} />;
  if (kind === 'semantic') return <SemanticEditor node={node} update={update} target={target || schema?.target || 'run'} graph={graph} nodeId={nodeId} readOnly={readOnly} teamId={teamId} agentName={agentName} />;
  return <div className="text-[12px] text-[color:var(--text-muted)]">This node uses the “{kind}” instrument, which has no editor yet. Its configuration is kept as saved.</div>;
}

// ---------------------------------------------------------------------------
// The panel
// ---------------------------------------------------------------------------

export default function NodeConfigPanel({
  graph, nodeId, onChange, onDelete, onClose, onNewVerdict, onNewStep,
  schema, scopes = null, target = null, integrations = [], modelsFor = () => [], teamId = null, agentName = null,
  problem = null, step = null, testing = false, onRunTest = null, canTest = true, readOnly = false,
}) {
  const node = graph?.nodes?.[nodeId];
  const kind = kindOf(node);
  const meta = kindMeta(kind);
  const [tab, setTab] = useState('config');
  const [width, setWidth] = useState(readWidth);
  const widthRef = useRef(width);
  widthRef.current = width;

  useEffect(() => { setTab('config'); }, [nodeId]);
  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  const startResize = useCallback((e) => {
    e.preventDefault();
    const startX = e.clientX;
    const startW = widthRef.current;
    const onMove = (ev) => setWidth(Math.min(MAX_W, Math.max(MIN_W, startW + (startX - ev.clientX))));
    const onUp = () => {
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup', onUp);
      try { localStorage.setItem(WIDTH_KEY, String(widthRef.current)); } catch (_) { /* private mode */ }
    };
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
  }, []);

  if (!node) return null;
  const update = readOnly ? () => {} : (patch) => onChange(nodeId, patch);
  const verdict = kind === 'verdict';
  // A step with one output routes by OUTCOME; anything else keeps the expression editor.
  const shape = verdict ? null : outcomes.read(node);


  return (
    <aside className="relative flex h-full shrink-0 flex-col border-l border-[color:var(--border-default)] bg-[color:var(--surface-base)]" style={{ width, fontFamily: 'Inter, sans-serif' }} aria-label={`${meta.label} settings`}>
      <div onMouseDown={startResize} title="Drag to resize" className="absolute left-0 top-0 bottom-0 z-10 -ml-0.5 w-1 cursor-col-resize hover:bg-[color:var(--trodo-primary)]/30" />

      <div className="shrink-0 border-b border-[color:var(--border-default)] px-3 py-3">
        <div className="flex items-center gap-2.5">
          <NodeMark kind={kind} node={node} size={30} />
          <input type="text" value={node.name || ''} readOnly={readOnly} aria-label="Step name" placeholder={meta.label} title={`${meta.label} · ${nodeId}`}
            onChange={(e) => update({ name: e.target.value })}
            className="-ml-1.5 min-w-0 flex-1 rounded-md bg-transparent px-1.5 py-0.5 text-[13.5px] font-semibold text-[color:var(--text-primary)] focus:bg-[color:var(--surface-sunken)] focus:outline-none" />
          <div className="flex shrink-0 items-center gap-0.5">
            {!readOnly && (
              <button type="button" onClick={() => onDelete(nodeId)} title="Delete step" aria-label="Delete step" className="rounded-md p-1.5 text-[color:var(--feedback-error)] hover:bg-[color:var(--feedback-error)]/10"><Trash2 className="h-3.5 w-3.5" /></button>
            )}
            <button type="button" onClick={onClose} title="Close" aria-label="Close" className="rounded-md p-1.5 text-[color:var(--text-muted)] hover:bg-surface-hover"><X className="h-4 w-4" /></button>
          </div>
        </div>
        {problem && <div className="mt-2 text-[11px] text-[color:var(--feedback-error)]">{problem}</div>}
      </div>

      <div className="flex shrink-0 items-center justify-between border-b border-[color:var(--border-default)] px-2 py-1.5">
        <SegmentedTabs size="sm" ariaLabel="Step panel" value={tab} onChange={setTab}
          options={[{ value: 'config', label: 'Settings' }, ...(verdict ? [] : [{ value: 'routing', label: 'Output & routing' }]), { value: 'test', label: 'Test' }]} />
        {canTest && onRunTest && !verdict && (
          <button type="button" onClick={() => { setTab('test'); onRunTest(nodeId, 'until'); }} disabled={testing} title="Run the eval up to and including this step on a real trace"
            className="mr-2 inline-flex items-center gap-1.5 rounded-lg border border-[color:var(--border-default)] bg-[color:var(--surface-base)] px-2.5 py-1 text-[12px] font-medium text-[color:var(--text-primary)] hover:bg-surface-hover disabled:opacity-50">
            {testing ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Play className="h-3.5 w-3.5" />} Test
          </button>
        )}
      </div>

      {tab === 'config' && (
        <fieldset disabled={readOnly} className="min-h-0 min-w-0 flex-1 overflow-y-auto px-3 py-3.5" style={{ border: 0, margin: 0 }}>
          <KindEditor kind={kind} node={node} update={update} graph={graph} nodeId={nodeId} readOnly={readOnly}
            schema={schema} scopes={scopes} target={target} integrations={integrations} modelsFor={modelsFor} teamId={teamId} agentName={agentName} />
        </fieldset>
      )}

      {tab === 'routing' && !verdict && (
        <fieldset disabled={readOnly} className="min-h-0 min-w-0 flex-1 overflow-y-auto px-3 py-3.5" style={{ border: 0, margin: 0 }}>
          {kind === 'decision_model' ? (
            <Section title="Routes">
              <DecisionRoutes graph={graph} nodeId={nodeId} readOnly={readOnly} onChange={(edges) => update({ edges })}
                onNewVerdict={(i, passed) => onNewVerdict(nodeId, i, passed)} onNewStep={(i) => onNewStep(nodeId, i)} />
            </Section>
          ) : shape ? (
            <Section title="This step returns">
              <OutcomesEditor graph={graph} nodeId={nodeId} shape={shape} readOnly={readOnly}
                lockShape={['human', 'ui_filter', 'semantic', 'decision_model'].includes(kind)}
                onChange={(patch) => update(patch)}
                onNewVerdict={(i, passed) => onNewVerdict(nodeId, i, passed)} onNewStep={(i) => onNewStep(nodeId, i)} />
            </Section>
          ) : (
            /* Two outputs, or a condition something else wrote: the expression
               editor, rather than flattening a graph we cannot read back. */
            <>
              <Section title="Output" hint="what this step returns; the routes below branch on it">
                <OutputFieldsEditor value={node.output} readOnly={readOnly} onChange={(output) => update({ output })} />
              </Section>
              <Section title="Then go to" hint="first matching condition wins; every step needs an otherwise">
                <EdgesEditor graph={graph} nodeId={nodeId} readOnly={readOnly} onChange={(edges) => update({ edges })}
                  onNewVerdict={(edgeIndex, passed) => onNewVerdict(nodeId, edgeIndex, passed)} onNewStep={(edgeIndex) => onNewStep(nodeId, edgeIndex)} />
              </Section>
            </>
          )}
        </fieldset>
      )}

      {tab === 'test' && (
        <div className="min-h-0 flex-1 overflow-y-auto px-3 py-3.5">
          {!verdict && onRunTest && (
            <div className="mb-3 flex flex-wrap items-center gap-1.5">
              <button type="button" disabled={testing || !canTest} onClick={() => onRunTest(nodeId, 'only')} className="inline-flex h-7 items-center gap-1 rounded-md border border-[color:var(--border-default)] px-2.5 text-[12px] text-[color:var(--text-primary)] hover:bg-surface-hover disabled:opacity-50">
                <Play className="h-3 w-3" /> Run only this step
              </button>
              <button type="button" disabled={testing || !canTest} onClick={() => onRunTest(nodeId, 'until')} className="inline-flex h-7 items-center gap-1 rounded-md border border-[color:var(--border-default)] px-2.5 text-[12px] text-[color:var(--text-primary)] hover:bg-surface-hover disabled:opacity-50">
                <Play className="h-3 w-3" /> Run up to here
              </button>
              {!canTest && <span className="text-[11px] text-[color:var(--text-muted)]">Pick a trace to test on first (Test, in the top bar).</span>}
            </div>
          )}
          {step ? <ol className="text-[12px]" aria-label="This step's result"><StepRow step={step} graph={graph} last /></ol> : (
            <div className="text-[12px] text-[color:var(--text-muted)]">{verdict ? 'A verdict has nothing to run: it is where the walk ends.' : 'Nothing has run for this step yet.'}</div>
          )}
        </div>
      )}
    </aside>
  );
}
